import type { CSSProperties, ReactNode } from 'react';

/**
 * A selectable pill (gap G31).
 *
 * Eight dashboard routes each carried a private `chipStyle(selected)` helper
 * that was byte-identical apart from its padding — filter chips, audience
 * chips, payment-method chips, vehicle-type chips. Eight copies of one visual
 * idea is how a design language stops being one: the moment somebody adjusts
 * the selected state, seven screens quietly disagree with the eighth.
 *
 * The selected state deliberately changes THREE things at once — background,
 * border and text colour — rather than only the border. A chip whose selection
 * is a one-pixel outline is a chip people fail to notice they have toggled, and
 * it is invisible to anyone with reduced colour vision.
 */
export interface ChipProps {
  readonly children: ReactNode;
  readonly selected?: boolean;
  /**
   * Vertical padding varies by context — a filter row is tighter than a till
   * keypad — so it is a prop rather than eight forks of the component.
   */
  readonly size?: 'sm' | 'md';
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly style?: CSSProperties;
}

export function Chip({
  children,
  selected = false,
  size = 'sm',
  onClick,
  disabled,
  title,
  style,
}: ChipProps) {
  return (
    <button
      type="button"
      className="k-press"
      onClick={onClick}
      disabled={disabled}
      title={title}
      // A chip is a toggle, and a screen reader user needs its state, not just
      // its label — the visual change alone says nothing to them.
      aria-pressed={selected}
      style={{ ...chipStyle(selected, size), ...style }}
    >
      {children}
    </button>
  );
}

/**
 * The chip's own styling, exported for the few places that need the style
 * without the element (a label wrapping a hidden radio, for instance).
 */
export function chipStyle(selected: boolean, size: 'sm' | 'md' = 'sm'): CSSProperties {
  return {
    padding: size === 'md' ? '10px 14px' : '8px 14px',
    minHeight: 'var(--touch-target, 44px)',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
    background: selected ? 'var(--color-primary)' : 'transparent',
    color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    // filter joins the list so the shared pointer-response hover eases here
    // too — an inline transition replaces the stylesheet's rather than
    // merging with it.
    transition:
      'background 160ms ease, border-color 160ms ease, color 160ms ease, filter 120ms ease',
  };
}
